'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ROUTES } from '@/utils/constants';
import { removeProductFromUid } from '@/utils/url/url';
import { useStore } from '@/store/store';
import { SvgLoader } from '../svg/loader';

import './styles/@theme';
import { getNavigatedToProps } from '@/tracking/amplitude/events/general/navigation-link-clicked';
import { toSnakeCase } from '@/tracking/amplitude/utils';
import { useSnowplowUm } from '@/tracking/snowplow';
import { EVENT_TYPE } from '@/tracking/snowplow/utils';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import { getGameZoneInteractionProps, isInTrackedGameZone } from '@/utils/tracking/game-zone-interaction';
import { getMlRecommendationContexts } from '@/tracking/snowplow/ml-recommendation';
import type { MlRecommendationTracking } from '@/types/tracking/ml-recommendation';

type CategoryViewAllProps = {
    categoryUid: string;
    zoneName: string;
    isInfluencerEntryPoint?: boolean;
    ctaLabel?: string;
    mlRecommendation?: MlRecommendationTracking | null;
};

export const CategoryViewAll: React.FC<CategoryViewAllProps> = ({
    categoryUid,
    zoneName,
    isInfluencerEntryPoint = false,
    ctaLabel,
    mlRecommendation = null
}) => {
    const currentPath = usePathname();
    const handleShowLoading = useStore((s) => s.loading.handleShowLoading);
    const { sendEvent } = useStore((state) => state.tracking);
    const { sendEventToSnowplow } = useSnowplowUm();

    const categoryUrl = isInfluencerEntryPoint
        ? `${ROUTES.INFLUENCER_HUB}${categoryUid}`
        : `${ROUTES.CATEGORY}${removeProductFromUid(categoryUid)}`;

    const viewAllCtaText = isInfluencerEntryPoint ? ctaLabel || 'Play and watch' : 'View all';

    if (!isInfluencerEntryPoint && currentPath?.startsWith(ROUTES.CATEGORY) && currentPath !== categoryUrl) {
        return null;
    }

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        const isPrimaryClick = event.button === 0;
        const isActive = currentPath === categoryUrl;

        const eventProps = getNavigatedToProps({
            module: zoneName,
            linkText: viewAllCtaText,
            linkUrl: categoryUrl
        });
        sendEvent([{ destination: 'amplitude', eventProps }]);

        if (isInTrackedGameZone(event.currentTarget)) {
            const snowplowCtaLabel = toSnakeCase(viewAllCtaText);
            const zoneInteractionProps = getGameZoneInteractionProps({
                action: 'click',
                element: event.currentTarget,
                pathname: currentPath ?? '',
                ctaLabel: snowplowCtaLabel
            });

            void (async () => {
                try {
                    await ensureCoreEventsInitialized();
                    sendEventToSnowplow({
                        event: EVENT_TYPE.ZONE_INTERACTION,
                        eventProps: zoneInteractionProps,
                        contexts: getMlRecommendationContexts(mlRecommendation)
                    });
                } catch {
                    // Logged in ensureCoreEventsInitialized.
                }
            })();
        }

        if (isPrimaryClick) {
            if (isActive) {
                event.preventDefault();
                return;
            }
            handleShowLoading();
        }
    };

    return (
        <Link
            data-testid="category-view-all-button"
            className="category-view-all"
            href={categoryUrl}
            onClick={handleClick}
            prefetch={false}>
            {viewAllCtaText} <SvgLoader iconName="arrow-right" />
        </Link>
    );
};
